import React from "react";
import { ReleaseVersionIssue } from "../../global/Types";

interface ReleaseVersionsTableProps {
    issues: ReleaseVersionIssue[];
    onRowClick: (issue: ReleaseVersionIssue) => void;
}
const headerLabels: Record<string, string> = {
    "ticket_number": "Ticket",
    "summary": "Summary",
    "status": "Status",
    "status_category": "Category",
    "assignee": "Assignee",
    "priority": "Priority",
};

const headers = [
    "ticket_number",
    "summary",
    "status",
    "status_category",
    "assignee",
    "priority",
];

const truncate = (str: string, n = 24) =>
    str && str.length > n ? str.slice(0, n) + "..." : str;

const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
        "Ready for Development": "bg-gray-100 text-gray-600",
        "Design Review": "bg-gray-100 text-gray-600",
        "In Progress": "bg-blue-100 text-blue-700",
        "Dev Complete": "bg-indigo-100 text-indigo-700",
        "Staging & QA Testing": "bg-purple-100 text-purple-700",
        "Staging testing": "bg-purple-100 text-purple-700",
        "Ready for Staging": "bg-emerald-100 text-emerald-800",
        "Ready for Production": "bg-emerald-200 text-emerald-900",
        "Recently Closed": "bg-emerald-200 text-emerald-900",
        "Closed": "bg-emerald-200 text-emerald-900",
    };
    const style = styles[status] ?? "bg-gray-100 text-gray-600";
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style}`}>
            {status}
        </span>
    );
};

const getStatusCategoryBadge = (category: string) => {
    const styles: Record<string, string> = {
        "To Do": "bg-gray-100 text-gray-600",
        "In Progress": "bg-blue-200 text-blue-800",
        "Done": "bg-emerald-200 text-emerald-900",
    };
    const style = styles[category] ?? "bg-gray-200 text-gray-700";
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style}`}>
            {category}
        </span>
    );
};

const getStatusCategoryColor = (statusCategory: string) => {
    switch (statusCategory.toLowerCase()) {
        case "to do":      return "bg-gray-200";
        case "in progress": return "bg-yellow-50";
        case "done":       return "bg-green-400 bg-opacity-30";
        default:           return "";
    }
};

const getPriorityBadge = (priority: string) => {
    const styles: Record<string, { color: string; label: string }> = {
        "Highest": { color: "#DC2626", label: "Highest" },
        "High":    { color: "#EA580C", label: "High" },
        "Medium":  { color: "#CA8A04", label: "Medium" },
        "Low":     { color: "#2563EB", label: "Low" },
        "Lowest":  { color: "#9CA3AF", label: "Lowest" },
    };
    const entry = styles[priority] ?? { color: "#9CA3AF", label: priority };
    return (
        <span className="inline-flex items-center gap-1.5">
            <span style={{ backgroundColor: entry.color }} className="w-2.5 h-2.5 rounded-full flex-shrink-0" />
            <span className="text-xs text-gray-700">{entry.label}</span>
        </span>
    );
};

const getAssigneeBadge = (assignee: string) => {
    if (!assignee || assignee === "Unassigned")
        return <span className="text-gray-400 text-xs">Unassigned</span>;
    return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 text-xs font-medium whitespace-nowrap">
            {assignee}
        </span>
    );
};

const ReleaseVersionsTable: React.FC<ReleaseVersionsTableProps> = ({ issues, onRowClick }) => {
    if (!issues.length) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center">
                <svg className="w-8 h-8 text-gray-300 mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
                <p className="text-sm font-medium text-gray-500">No issues found for this fix version</p>
                <p className="text-xs text-gray-400 mt-1">This version may not have any linked Jira tickets yet</p>
            </div>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-indigo-100 rounded-lg shadow-md bg-white">
                <thead className="sticky top-0 z-10">
                <tr>
                    {headers.map((header) => (
                        <th
                            key={header}
                            className="px-4 py-3 text-xs font-semibold text-indigo-700 bg-indigo-50 border-b-2 border-indigo-200 text-left tracking-wide whitespace-nowrap"
                        >
                            {headerLabels[header] ?? header}
                        </th>
                    ))}
                    <th className="px-4 py-3 bg-indigo-50 border-b-2 border-indigo-200 w-10" />
                </tr>
                </thead>
                <tbody className="align-top">
                {issues.map((issue) => {
                    const rowColor = getStatusCategoryColor((issue as any)["status_category"] || "");
                    const hasBranchOnlyCommits = issue.branch_only_commits?.length > 0;
                    return (
                        <tr
                            key={issue.id}
                            className={`transition-all cursor-pointer ${rowColor} hover:bg-indigo-100 hover:shadow-sm`}
                            style={hasBranchOnlyCommits ? { borderLeft: "3px solid #F59E0B" } : {}}
                            onClick={() => onRowClick(issue)}
                        >
                            {headers.map((header) => {
                                let value = (issue as any)[header];
                                if (Array.isArray(value)) value = value.join(", ");
                                if (typeof value !== "string") value = String(value ?? "");
                                return (
                                    <td
                                        key={header}
                                        style={{ verticalAlign: "top" }}
                                        className={`px-4 py-3 text-xs text-gray-800 border-b border-indigo-50 ${
                                            header === "summary" || header === "assignee"
                                                ? "whitespace-normal break-words max-w-xs"
                                                : "truncate whitespace-nowrap max-w-xs"
                                        }`}
                                    >
                                        {header === "ticket_number"
                                            ? <a
                                                href={issue.issue_url ?? "#"}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-indigo-600 font-medium hover:underline"
                                                onClick={e => e.stopPropagation()}
                                              >
                                                {value}
                                              </a>
                                            : header === "status"         ? getStatusBadge(value)
                                            : header === "status_category" ? getStatusCategoryBadge(value)
                                            : header === "priority"       ? getPriorityBadge(value)
                                            : header === "assignee"       ? getAssigneeBadge(value)
                                            : header === "summary"        ? value
                                            : truncate(value)}
                                    </td>
                                );
                            })}
                            <td style={{ verticalAlign: "top" }} className="px-4 py-3 border-b border-indigo-50">
                                {hasBranchOnlyCommits && (
                                    <span
                                        title="These commits were linked via branch name only — ticket not mentioned in commit message"
                                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300 whitespace-nowrap"
                                    >
                                        <svg viewBox="0 0 16 16" fill="none" className="w-3 h-3 flex-shrink-0">
                                            <path d="M8 2L14.5 13.5H1.5L8 2Z" fill="#D97706"/>
                                            <rect x="7.3" y="6" width="1.4" height="4" rx="0.7" fill="white"/>
                                            <rect x="7.3" y="10.8" width="1.4" height="1.4" rx="0.7" fill="white"/>
                                        </svg>
                                        {issue.branch_only_commits.length} {issue.branch_only_commits.length === 1 ? "commit" : "commits"} missing ticket ref
                                    </span>
                                )}
                            </td>
                        </tr>
                    );
                })}
                </tbody>
            </table>
        </div>
    );
};

export default ReleaseVersionsTable;